<template>
  <v-card>
    <v-card-title class="primary white--text">
      <v-row>
        <v-col>
          <span>
            {{ $t('harvestPeriod') }}
          </span>
        </v-col>

        <v-col cols="auto">
          <BaseDialogActions hideFullscreen hideRefresh></BaseDialogActions>
        </v-col>
      </v-row>
    </v-card-title>

    <v-card-text class="pt-4">
      <v-row v-if="initialized">
        <v-col cols="12">
          <DatePicker
          @date-picked="startDatePicked"
          clearable
          elementId="tract-harvest-start-date"
          :startDate="harvestStart"
          :min="tract.availabilityStartDate"
          :max="harvestStartMax"
          :hint="startHint"
          :dateLabel="$t('harvestStart')"/>
        </v-col>
        <v-col cols="12">
          <DatePicker
          @date-picked="endDatePicked"
          clearable
          elementId="tract-harvest-end-date"
          :startDate="harvestEnd"
          :min="harvestStart"
          :hint="endHint"
          :dateLabel="$t('harvestEnd')"/>
        </v-col>
        <v-col cols="12" v-if="availabilityExtensionNote">
          <span class="grey--text text--darken-1">{{ availabilityExtensionNote }}</span>
        </v-col>
        <v-col cols="12" v-if="validationError">
          <span class="error--text">{{ validationError }}</span>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions class="pb-4 px-0">
      <v-row justify="end" align="center">
        <v-col cols="auto" v-if="originalTract?.tractId && didHarvestDatesChange">
          <v-checkbox
          v-model="applyDateChangesToContracts"
          color="primary"
          :label="$t('applyHarvestDatesToContracts')"/>
        </v-col>
        <v-col cols="auto">
          <v-btn @click="onConfirm" color="primary" :disabled="!!validationError">{{$t('confirm')}}</v-btn>
        </v-col>
      </v-row>
    </v-card-actions>
  </v-card>
</template>

<script>
import moment from 'moment'

export default {
  name: 'HarvestPeriodForm',

  components: {
    DatePicker: () => import('@/components/helper/DatePicker.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue')
  },

  props: {
    tract: {
      type: Object,
      required: true
    },

    originalTract: {
      type: Object,
      required: true
    },

    startHint: {
      type: String,
      default: ''
    },

    endHint: {
      type: String,
      default: ''
    }
  },

  data: () => ({
    applyDateChangesToContracts: false,
    harvestStart: null,
    harvestEnd: null,
    initialized: false
  }),

  mounted () {
    this.harvestStart = this.tract.harvestStartDate
    this.harvestEnd = this.tract.harvestEndDate
    this.initialized = true
  },

  watch: {
    didHarvestDatesChange (i) {
      if (!i) {
        this.applyDateChangesToContracts = false
      }
    }
  },

  computed: {
    didHarvestDatesChange () {
      if (this.harvestStart === null || this.harvestEnd === null) return false
      const isStartDayEqual = moment(this.harvestStart).isSame(moment(this.originalTract.harvestStartDate), 'day')
      const isEndDayEqual = moment(this.harvestEnd).isSame(moment(this.originalTract.harvestEndDate), 'day')
      return !(isStartDayEqual && isEndDayEqual)
    },

    isEndBeforeStart () {
      if (!this.harvestStart || !this.harvestEnd) return false
      return moment(this.harvestEnd).isBefore(moment(this.harvestStart), 'day')
    },

    isStartBeforeAvailability () {
      const { availabilityStartDate } = this.tract
      if (!availabilityStartDate || !this.harvestStart) return false
      return moment(this.harvestStart).isBefore(moment(availabilityStartDate), 'day')
    },

    extendedAvailabilityEnd () {
      const { availabilityEndDate } = this.tract
      if (!availabilityEndDate || !this.harvestEnd) return null
      return moment(this.harvestEnd).isAfter(moment(availabilityEndDate), 'day') ? this.harvestEnd : null
    },

    harvestStartMax () {
      return this.harvestEnd ?? this.tract.availabilityEndDate
    },

    availabilityExtensionNote () {
      if (!this.extendedAvailabilityEnd) return ''
      return this.$t('availabilityEndExtendedToHarvestEnd', { date: moment(this.extendedAvailabilityEnd).format('MM/DD/YYYY') })
    },

    isBeforePurchase () {
      if (!this.tract.purchaseDate || !this.harvestStart) return false
      return moment(this.harvestStart).isBefore(moment(this.tract.purchaseDate), 'day')
    },

    validationError () {
      if (this.isEndBeforeStart) return this.$t('startDateAfterEndDate')
      if (this.isStartBeforeAvailability) return this.$t('harvestWithinAvailability')
      if (this.isBeforePurchase) return this.$t('purchaseBeyondHarvestStart')
      return ''
    }
  },

  methods: {
    startDatePicked (date) {
      this.harvestStart = date
    },

    endDatePicked (date) {
      this.harvestEnd = date
    },

    onConfirm () {
      this.$emit('confirm', {
        harvestStart: this.harvestStart,
        harvestEnd: this.harvestEnd,
        availabilityEnd: this.extendedAvailabilityEnd ?? this.tract.availabilityEndDate,
        applyToContracts: this.applyDateChangesToContracts
      })
    }
  }
}
</script>
